/**
 * deal-detail/render-sourcing.ts — 소싱 렌더 (니즈 리스트 / 니즈 생성 모달 / RFQ 버블·보기 모달)
 *
 * DealDetailView.ts 에서 분리 (정리 Step 3a, 2026-07-03 — 분해 계획 v2 §2).
 * 이동만 — 동작·마크업 변화 없음. 전부 "state → HTML 문자열" 순수 함수.
 * 이벤트는 data-action 배선이라 핸들러는 뷰 메서드 그대로.
 */

import * as api from '../../api/client';
import { escapeHtml, fmtDateTime, catalogHitDescription } from '../../lib/cells';
import { renderRfqBubbleMeta } from '../../lib/rfqBubble';
import { renderRegistrationChip } from '../../lib/condition-chip';
import { CONDITION_OPTIONS } from '../../lib/condition';
import { type DealDetailData, type NeedEditState } from './types';

// 뷰 state 의 부분 타입 — DealDetailData 의 인라인 정의를 그대로 참조 (드리프트 방지)
type NeedCreateState = DealDetailData['needCreate'];
type TagEditorState  = DealDetailData['tagEditor'];

// ─── 소싱 섹션 (P9-UI Step 2) ───────────────────────────────────────
// need 평탄 리스트. 한 row = 한 need.
// "원문 보기" 클릭 → /need/:id/rfqs 새 탭 (NeedRfqsView).
export function renderSourcingSection(
  needs:      api.SourcingNeedRow[],
  needMenuId: string | null,
  tagEditor:  TagEditorState,
): string {
  // 니즈가 0 개여도 섹션은 표시 — '니즈 생성' 버튼으로 첫 니즈를 만들 수 있어야 함 (§4.8.10)

  // 딜 상세엔 '이 딜에 연결된 니즈'만 표시 (미연결은 소싱 페이지에서만 — 현민 요청).
  const linked = needs.filter((n) => n.deal_id != null);

  const head = `
      <tr>
        <th>생성일시</th>
        <th>파트넘버</th>
        <th>디스크립션</th>
        <th>컨디션</th>
        <th class="text-right">수량</th>
        <th>영업 요청</th>
        <th>소싱 메모</th>
        <th>RFQ</th>
        <th class="text-center" style="width: 56px;">작업</th>
      </tr>`;

  const body = linked.length === 0
    ? `<tr><td colspan="9" class="text-muted text-center">생성된 니즈가 없습니다. 품목에서 선택 후 '니즈 생성'으로 추가하세요.</td></tr>`
    : linked.map((n) => renderSourcingNeedRow(n, needMenuId, tagEditor)).join('');

  return `
      <section class="deal-section">
        <h2 class="deal-section-title">소싱</h2>
        <div class="deal-table-wrap">
          <table class="deal-sourcing-table deal-sourcing-table-v2">
            <thead>${head}</thead>
            <tbody>${body}</tbody>
          </table>
        </div>
      </section>
    `;
}

export function renderNeedSearchResultsHtml(m: NeedCreateState): string {
  if (m.searching) return '<div class="deal-item-search-empty">검색 중…</div>';
  if (m.query.trim().length < 2) return '<div class="deal-item-search-empty">2글자 이상 입력해 주세요.</div>';
  if (m.hits.length === 0) return '<div class="deal-item-search-empty">일치하는 품목이 없습니다.</div>';
  return m.hits.map((hit) => {
    const key = `${hit.source}::${hit.option_part}`;
    const desc = catalogHitDescription(hit);
    return `<button class="deal-item-search-row" type="button"
                      data-action="need-create-pick" data-key="${escapeHtml(key)}">
                <code class="deal-item-search-part">${escapeHtml(hit.option_part)}</code>
                <span class="deal-item-search-desc" title="${escapeHtml(desc)}">${escapeHtml(desc)}</span>
              </button>`;
  }).join('');
}

// 니즈 생성 모달 (딜 상세 — §4.8.10-14, "이 딜에 연결" 기본)
export function renderNeedCreateModal(m: NeedCreateState): string {
  if (!m.open) return '';
  const condOpts = `<option value="">컨디션 선택</option>` +
    CONDITION_OPTIONS.map((c) =>
      `<option value="${escapeHtml(c)}"${c === m.condition ? ' selected' : ''}>${escapeHtml(c)}</option>`).join('');
  const tagBoxes = api.SALES_REQUEST_TAGS.map((t) =>
    `<label class="deal-tag-opt">
         <input type="checkbox" ${m.salesTags.includes(t) ? 'checked' : ''}
                data-action="need-create-tag" data-tag="${escapeHtml(t)}" /> ${escapeHtml(t)}
       </label>`).join('');
  return `
      <div class="deal-rfq-modal" data-need-create-modal>
        <div class="deal-rfq-modal-overlay" data-action="need-create-overlay"></div>
        <div class="deal-rfq-modal-dialog deal-need-create-dialog" role="dialog" aria-label="니즈 생성">
          <header class="deal-rfq-modal-header">
            <h2>니즈 생성</h2>
            <button class="deal-rfq-modal-close" type="button" data-action="need-create-close" aria-label="닫기">✕</button>
          </header>
          <div class="deal-rfq-modal-body">
            <p class="text-muted text-sm">소싱 니즈의 필수값을 입력합니다. (품목, 컨디션, 수량 필수)</p>
            <div class="deal-need-form">
              <div class="deal-need-field deal-need-field-wide">
                <span>품목 검색</span>
                ${m.option_part || m.item_text
                  ? `<div class="deal-need-picked">
                       ${m.option_part ? `<code>${escapeHtml(m.option_part)}</code>` : ''}
                       <span class="deal-need-picked-desc">${escapeHtml(m.item_text || '-')}</span>
                       <button class="btn btn-ghost btn-sm" type="button" data-action="need-create-clear">변경</button>
                     </div>`
                  : `<input type="text" class="deal-item-search-input" data-action="need-create-search"
                            value="${escapeHtml(m.query)}" placeholder="파트넘버 또는 디스크립션 검색 (2글자 이상)" autocomplete="off" />
                     <div class="deal-item-search-results" id="need-search-results">
                       ${renderNeedSearchResultsHtml(m)}
                     </div>`}
              </div>
              <label class="deal-need-field">
                <span>컨디션</span>
                <select data-action="need-create-condition" data-field="condition">${condOpts}</select>
              </label>
              <label class="deal-need-field">
                <span>수량</span>
                <input type="number" min="1" step="1" data-action="need-create-field" data-field="quantity"
                       value="${escapeHtml(m.quantity)}" />
              </label>
              <div class="deal-need-field deal-need-field-wide">
                <span>영업 요청</span>
                <div class="deal-need-tags">${tagBoxes}</div>
              </div>
              <div class="deal-need-field deal-need-field-wide">
                <span>딜 연결</span>
                <div class="deal-need-link-radios">
                  <label><input type="radio" name="need-deal-link"
                          data-action="need-create-link" data-linked="1"
                          ${m.dealLinked ? 'checked' : ''} /> 이 딜에 연결</label>
                  <label><input type="radio" name="need-deal-link"
                          data-action="need-create-link" data-linked="0"
                          ${!m.dealLinked ? 'checked' : ''} /> 딜 연결 없음</label>
                </div>
              </div>
            </div>
          </div>
          <footer class="deal-rfq-modal-footer">
            <button class="btn btn-ghost" type="button" data-action="need-create-close" ${m.saving ? 'disabled' : ''}>취소</button>
            <button class="btn btn-primary" type="button" data-action="need-create-submit" ${m.saving ? 'disabled' : ''}>
              ${m.saving ? '생성 중…' : '생성'}
            </button>
          </footer>
        </div>
      </div>
    `;
}

function renderSourcingNeedRow(
  n:          api.SourcingNeedRow,
  needMenuId: string | null,
  tagEditor:  TagEditorState,
): string {
  // 니즈 행 "구매요청" 표시 = 니즈 레벨 플래그 OR 이 니즈의 rfq 중 하나라도 구매요청됨(마이그140).
  //   (⋯메뉴의 토글은 여전히 니즈 레벨 n.purchase_requested 만 on/off)
  const isRequested = n.purchase_requested || n.rfqs.some((r) => r.purchase_requested);
  const reqPill = isRequested
    ? ` <span class="need-purchase-pill">🛒 구매 요청</span>`
    : '';
  return `
      <tr class="${isRequested ? 'need-row-requested' : ''}">
        <td class="text-sm text-muted">${escapeHtml(fmtDateTime(n.created_at))}</td>
        <td>${n.option_part
              ? `<code>${escapeHtml(n.option_part)}</code>`
              : renderRegistrationChip(n.option_part)}${reqPill}</td>
        <td>${escapeHtml(n.item_text ?? '-')}</td>
        <td>${escapeHtml(n.requested_condition ?? '-')}</td>
        <td class="text-right">${n.quantity} EA${renderNeedOrigin(n)}</td>
        <td>${renderTagCell(n, 'sales', n.sales_request_tags, '영업 요청', tagEditor)}</td>
        <td>${renderTagCell(n, 'memo', n.sourcing_memo_tags, '소싱 메모', tagEditor)}</td>
        <td><div class="deal-rfq-bubbles">${renderRfqBubbles(n.rfqs)}</div></td>
        <td class="text-center deal-tag-cell">
          <button class="btn btn-ghost btn-icon deal-need-menu-trigger" type="button"
                  data-action="need-menu-open" data-need-id="${escapeHtml(n.id)}"
                  title="니즈 작업" aria-label="니즈 작업">⋯</button>
          ${needMenuId === n.id ? `
            <div class="deal-tag-pop-overlay" data-action="need-menu-close"></div>
            <div class="deal-tag-pop deal-need-menu">
              <button class="deal-tag-opt" type="button"
                      style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                      data-action="need-edit" data-need-id="${escapeHtml(n.id)}">니즈 수정</button>
              <button class="deal-tag-opt text-danger" type="button"
                      style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                      data-action="need-delete" data-need-id="${escapeHtml(n.id)}">삭제</button>
            </div>` : ''}
        </td>
      </tr>
    `;
}

/**
 * 니즈 수량의 출처 (마이그 144 `need_origin`).
 *
 * 왜 보여 주는가: 서버에서 나온 니즈는 수량이 `구성 수량 × 서버 대수` 라서
 * 12EA 같은 숫자가 혼자 서 있으면 소싱 담당이 검증할 수 없다. 어느 서버의
 * 어느 라인에서 얼마씩 왔는지 그 자리에서 읽히게 한다.
 *
 * ★ 니즈는 **생성 시점의 요청 스냅샷**이다 — 나중에 서버 대수나 구성 수량이
 *   바뀌어도 니즈 수량을 자동으로 다시 계산하지 않는다. 구성이 바뀌었으면
 *   니즈를 새로 만든다(기존 니즈와 합치지 않는다. 병합은 한 번의 생성 요청
 *   안에서 같은 파트넘버·컨디션끼리만 일어난다).
 *
 *   그래서 "출처 합계 ≠ 니즈 수량" 은 **오류가 아니라 사실**이다 —
 *   이 니즈가 만들어진 뒤에 구성이 달라졌다는 뜻이다. 경고처럼 보이면
 *   고쳐야 할 것으로 오해하게 되므로 중립적으로 적는다.
 *   사람이 수량을 직접 고친 니즈(`qty_is_manual`)도 같은 이유로 정상이다.
 */
function renderNeedOrigin(n: api.SourcingNeedRow): string {
  const origins = n.origins ?? [];
  if (origins.length === 0) return '';

  const lines = origins.map((o) => {
    const where = o.server_model?.trim() || (o.server_item_id ? '서버' : '개별 품목');
    const math  = o.server_quantity && o.server_quantity > 1
      ? `${o.per_server_qty}개 × ${o.server_quantity}대 = ${o.contributed_qty}`
      : `${o.contributed_qty}개`;
    return `${where} · ${o.spec_category ?? ''} ${math}`.replace(/\s+/g, ' ').trim();
  });

  const sum   = origins.reduce((acc, o) => acc + o.contributed_qty, 0);
  const parts = [`출처 ${origins.length}건`];
  if (n.qty_is_manual) parts.push('직접 입력');
  // 생성 이후 구성이 바뀐 경우 — 지금 구성으로는 몇 개인지 알려만 준다.
  // (자동 반영하지 않는다. 필요하면 사람이 니즈를 새로 만든다)
  else if (sum !== n.quantity) parts.push(`현재 구성 기준 ${sum}EA`);

  return `
    <div class="need-origin" title="${escapeHtml(lines.join('\n'))}">
      <span class="need-origin-head">${escapeHtml(parts.join(' · '))}</span>
      ${lines.map((l) => `<span class="need-origin-line">${escapeHtml(l)}</span>`).join('')}
    </div>`;
}

// 태그 셀 — 클릭 시 compact 드롭다운(체크박스)으로 인라인 수정 (§4.8.4)
function renderTagCell(
  n:           api.SourcingNeedRow,
  kind:        'sales' | 'memo',
  tags:        string[],
  placeholder: string,
  tagEditor:   TagEditorState,
): string {
  const open = tagEditor?.needId === n.id && tagEditor?.kind === kind;
  // 기획: 앞 2개만 칩으로 표시, 초과분은 +N (admin-ux-preview.html 참조)
  const chips = tags.length === 0
    ? `<span class="text-muted text-sm">${placeholder}</span>`
    : tags.slice(0, 2).map((t) => `<span class="deal-need-tag">${escapeHtml(t)}</span>`).join(' ')
      + (tags.length > 2 ? ` <span class="deal-need-tag">+${tags.length - 2}</span>` : '');
  const options = kind === 'sales' ? api.SALES_REQUEST_TAGS : api.SOURCING_MEMO_TAGS;
  const popover = open ? `
      <div class="deal-tag-pop-overlay" data-action="need-tag-close"></div>
      <div class="deal-tag-pop deal-tag-editor-pop">
        ${options.map((opt) => {
          const checked = tags.includes(opt) ? 'checked' : '';
          return `<label class="deal-tag-opt">
            <input type="checkbox" ${checked}
                   data-action="need-tag-toggle" data-need-id="${escapeHtml(n.id)}"
                   data-kind="${kind}" data-tag="${escapeHtml(opt)}" />
            ${escapeHtml(opt)}
          </label>`;
        }).join('')}
      </div>` : '';
  return `
      <div class="deal-tag-cell">
        <button class="deal-tag-trigger" type="button"
                data-action="need-tag-open" data-need-id="${escapeHtml(n.id)}" data-kind="${kind}">
          ${chips}
        </button>
        ${popover}
      </div>
    `;
}

// RFQ 버블 — 매입처명 + native 가격(또는 재고 없음). 클릭 시 보기 모달.
function renderRfqBubbles(rfqs: api.SourcingRfqBubble[]): string {
  // 개별 니즈에서 숨긴(is_hidden) 매입처는 제외(마이그142).
  const visible = rfqs.filter((b) => !b.is_hidden);
  if (visible.length === 0) return '<span class="text-muted text-sm">-</span>';
  return visible.map((b) => {
    // 매입처 + 대체품목·대체컨디션·가격·RFQ메모·기타메모아이콘 (§5.5.18-20, 소싱과 동일)
    return `<button class="deal-rfq-bubble${b.purchase_requested ? ' is-purchase-requested' : ''}" type="button"
                      data-action="rfq-bubble" data-rfq-id="${escapeHtml(b.id)}">
                <span class="rfq-bubble-supplier">${b.purchase_requested ? '🛒 ' : ''}${escapeHtml(b.supplier_name)}</span>${renderRfqBubbleMeta(b)}
              </button>`;
  }).join('');
}

function fmtBubblePrice(b: api.SourcingRfqBubble): string {
  if (b.status === '재고 없음') return '재고 없음';
  if (b.price_amount == null) return '';
  if (b.price_currency === 'KRW') return `₩ ${b.price_amount.toLocaleString('ko-KR')}`;
  if (b.price_currency === 'USD') return `$${b.price_amount.toLocaleString('en-US')}`;
  return `${b.price_amount.toLocaleString()} ${b.price_currency ?? ''}`.trim();
}

// RFQ 버블 읽기전용 보기 모달 (딜 상세 — §4.8.9)
export function renderRfqViewModal(b: api.SourcingRfqBubble | null): string {
  if (!b) return '';
  const row = (label: string, value: string) =>
    `<div class="deal-rfq-view-row"><dt>${escapeHtml(label)}</dt><dd>${value}</dd></div>`;
  return `
      <div class="deal-rfq-modal" data-rfq-view-modal>
        <div class="deal-rfq-modal-overlay" data-action="rfq-view-overlay"></div>
        <div class="deal-rfq-modal-dialog" role="dialog" aria-label="RFQ 결과">
          <header class="deal-rfq-modal-header">
            <h2>${escapeHtml(b.supplier_name)}</h2>
            <button class="deal-rfq-modal-close" type="button" data-action="rfq-view-close" aria-label="닫기">✕</button>
          </header>
          <div class="deal-rfq-modal-body">
            <dl class="deal-rfq-view-list">
              ${row('가격', b.status === '재고 없음'
                ? '<span class="text-muted">재고 없음</span>'
                : escapeHtml(fmtBubblePrice(b) || '-'))}
              ${row('대체 컨디션', escapeHtml(b.sourced_condition ?? '-'))}
              ${row('대체품목', b.alternative_part ? `<code>${escapeHtml(b.alternative_part)}</code>` : '-')}
              ${row('RFQ 메모', b.rfq_memo_tags.length
                ? b.rfq_memo_tags.map((t) => `<span class="deal-need-tag">${escapeHtml(t)}</span>`).join(' ')
                : '-')}
              ${row('기타 메모', escapeHtml(b.request_note ?? '-'))}
              ${row('상태', escapeHtml(b.status))}
            </dl>
            ${b.attachments.length > 0 ? `
            <div class="deal-rfq-view-attachments">
              <div class="deal-rfq-view-attach-label">거래처 수신 이미지/PDF (${b.attachments.length})</div>
              <div class="deal-rfq-view-attach-grid">
                ${b.attachments.map((a) => a.mediaType.startsWith('image/')
                  ? `<a href="${escapeHtml(a.url)}" target="_blank" rel="noopener" class="deal-rfq-view-attach" title="${escapeHtml(a.origName ?? '')}"><img src="${escapeHtml(a.url)}" alt="${escapeHtml(a.origName ?? '')}" loading="lazy" /></a>`
                  : `<a href="${escapeHtml(a.url)}" target="_blank" rel="noopener" class="deal-rfq-view-attach is-doc" title="${escapeHtml(a.origName ?? '')}">📄 ${escapeHtml(a.origName ?? 'PDF')}</a>`
                ).join('')}
              </div>
            </div>` : ''}
          </div>
          <footer class="deal-rfq-modal-footer">
            ${(() => {
              // 가격 입력된 RFQ 만 구매요청 허용(재고없음·미입력 차단). 이미 요청됨이면 취소는 항상 가능.
              const canRequest = b.price_amount != null && b.status !== '재고 없음';
              const disabled = !b.purchase_requested && !canRequest;
              return `<button class="btn ${b.purchase_requested ? 'btn-secondary' : 'btn-primary'} btn-sm" type="button"
                    data-action="rfq-purchase-toggle" data-rfq-id="${escapeHtml(b.id)}"
                    data-requested="${b.purchase_requested ? '1' : '0'}" ${disabled ? 'disabled' : ''}
                    title="${disabled ? '가격이 입력된 거래처만 구매요청할 수 있습니다' : ''}">
                ${b.purchase_requested ? '🛒 구매요청 취소' : '🛒 구매요청'}
              </button>`;
            })()}
          </footer>
        </div>
      </div>
    `;
}

/** 니즈 수정 모달 — 요청 수량 + 컨디션만. 파트넘버·디스크립션은 읽기전용. */
export function renderNeedEditModal(s: NeedEditState): string {
  if (!s.open) return '';
  const opts = CONDITION_OPTIONS
    .map((c) => `<option value="${c}" ${c === s.condition ? 'selected' : ''}>${c}</option>`)
    .join('');
  return `
    <div class="deal-need-edit-wrap" data-need-edit-modal>
      <div class="deal-need-edit-overlay" data-action="need-edit-close"></div>
      <div class="deal-need-edit-modal" role="dialog" aria-label="\uB2C8\uC988 \uC218\uC815">
        <h3 class="deal-need-edit-title">\uB2C8\uC988 \uC218\uC815</h3>
        <p class="deal-need-edit-sub">\uC120\uD0DD\uD55C \uB2C8\uC988\uC758 \uC694\uCCAD \uC218\uB7C9\uACFC \uCEE8\uB514\uC158\uB9CC \uC218\uC815\uD569\uB2C8\uB2E4.</p>
        <div class="deal-need-edit-ro">
          <div><span class="deal-need-edit-rolabel">\uD30C\uD2B8\uB118\uBC84</span><span>${escapeHtml(s.partNumber || '-')}</span></div>
          <div><span class="deal-need-edit-rolabel">\uB514\uC2A4\uD06C\uB9BD\uC158</span><span>${escapeHtml(s.description || '-')}</span></div>
        </div>
        <div class="deal-need-edit-fields">
          <label class="deal-need-edit-flabel">\uC694\uCCAD \uC218\uB7C9
            <input class="input" type="number" min="1" step="1" data-action="need-edit-field" data-field="quantity" value="${escapeHtml(s.quantity)}" />
          </label>
          <label class="deal-need-edit-flabel">\uCEE8\uB514\uC158
            <select class="input" data-action="need-edit-field" data-field="condition">${opts}</select>
          </label>
        </div>
        ${s.error ? `<div class="deal-contact-err">${escapeHtml(s.error)}</div>` : ''}
        <div class="deal-need-edit-foot">
          <button class="btn btn-primary btn-sm" type="button" data-action="need-edit-save" ${s.saving ? 'disabled' : ''}>${s.saving ? '\uC800\uC7A5 \uC911\u2026' : '\uC800\uC7A5'}</button>
          <button class="btn btn-secondary btn-sm" type="button" data-action="need-edit-close">\uCDE8\uC18C</button>
        </div>
      </div>
    </div>
  `;
}
